import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import authService from '../services/authService';
import appConfig from '../config/appConfig';

const CourseFeesPage = () => {
  const [courseFees, setCourseFees] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState({}); // Track payment loading per fee ID
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const hasInitialFetch = useRef(false);

  // Fetch course fees data
  const fetchCourseFees = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      // Check authentication
      if (!authService.isAuthenticated()) {
        setError('Authentication required to view course fees.');
        setLoading(false);
        return;
      }
      
      const response = await apiService.getCourseFees();
      
      if (response.success && response.data) {
        // Handle paginated response structure: response.data.data.data contains the array
        if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          setCourseFees(response.data.data.data);
          // Extract pagination info
          const paginationData = {
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page,
            per_page: response.data.data.per_page,
            total: response.data.data.total,
            from: response.data.data.from,
            to: response.data.data.to,
            links: response.data.data.links
          };
          setPagination(paginationData);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          setCourseFees(response.data.data);
        } else if (Array.isArray(response.data)) {
          setCourseFees(response.data);
        } else if (response.data.course_fees && Array.isArray(response.data.course_fees)) {
          setCourseFees(response.data.course_fees);
        } else {
          setCourseFees([]);
        }
      } else {
        setError(response.data?.message || response.message || 'Failed to fetch course fees');
      }
    } catch (err) {
      setError('Error fetching course fees. Please try again.');
      console.error('Course fees fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle page change
  const handlePageChange = (page) => {
    fetchCourseFees(page);
  };

  // Load course fees on component mount
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchCourseFees();
    }
  }, [fetchCourseFees]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format course session (e.g., "012025" -> "Jan 2025", "062025" -> "June 2025")
  const formatCourseSession = (session) => {
    if (!session || session.length < 6) return session || 'N/A';
    
    // Extract month (first 2 digits) and year (last 4 digits)
    const monthStr = session.substring(0, 2);
    const yearStr = session.substring(2, 6);
    
    const monthNum = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    
    // Validate month
    if (monthNum < 1 || monthNum > 12) return session;
    
    // Month names
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    return `${monthNames[monthNum - 1]} ${year}`;
  };

  // Handle EASEBUZZ payment callback
  const handleEasebuzzPaymentCallback = async (paymentResponse, fee) => {
    try {
      setPaymentLoading(prev => ({ ...prev, [fee.id]: true }));
      setError('');
      setPaymentSuccess('');

      // If Easebuzz returned an error (e.g. user closed window, invalid access key), do not call verify
      if (paymentResponse && paymentResponse.error === true) {
        setError(paymentResponse.message || 'Payment was cancelled or failed.');
        setPaymentLoading(prev => ({ ...prev, [fee.id]: false }));
        return;
      }

      // Backend /api/easebuzz/verify requires: txnid, status, hash
      const hasRequiredFields = paymentResponse && paymentResponse.txnid && paymentResponse.status != null;
      if (!hasRequiredFields) {
        setError(paymentResponse?.message || 'Payment response incomplete. Please try again or contact support.');
        setPaymentLoading(prev => ({ ...prev, [fee.id]: false }));
        return;
      }

      const offered = parseFloat(fee.fee_offered) || 0;
      const received = parseFloat(fee.received_amt) || 0;
      const pending = offered - received;

      // Build verification payload with API-required fields (txnid, status, hash) and context
      const verificationData = {
        txnid: paymentResponse.txnid,
        status: paymentResponse.status,
        hash: paymentResponse.hash,
        amount: pending,
        course_fee_id: fee.id,
        course_session: fee.course_session,
        course_fee_structures_id: fee.course_fee_structures_id || null,
        ...paymentResponse
      };

      // Verify payment via backend API
      const response = await apiService.verifyEasebuzzPayment(verificationData);
      if (response.success && response.data && response.data.success) {
        setPaymentSuccess('Payment verified successfully! Refreshing course fees...');
        setError('');
        await fetchCourseFees();
        setTimeout(() => setPaymentSuccess(''), 3000);
      } else {
        setError(response.data?.message || 'Payment verification failed. Please contact support.');
        setPaymentSuccess('');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setError('Payment verification failed. Please contact support.');
      setPaymentSuccess('');
    } finally {
      setPaymentLoading(prev => ({ ...prev, [fee.id]: false }));
    }
  };

  // Handle Pay Now button click
  const handlePayNow = async (fee) => {
    const feeId = fee.id;
    const offered = parseFloat(fee.fee_offered) || 0;
    const received = parseFloat(fee.received_amt) || 0;
    const pending = offered - received;

    // Don't proceed if no pending amount
    if (pending <= 0) {
      return;
    }

    try {
      setPaymentLoading(prev => ({ ...prev, [feeId]: true }));
      setError('');
      setPaymentSuccess('');

      // Get user data for payment
      const userData = authService.getUserData();
      
      // Prepare payment data
      const paymentData = {
        amount: pending,
        currency: appConfig.payment.currency,
        description: `Course Fee Payment - ${formatCourseSession(fee.course_session)}`,
        course_session: fee.course_session,
        course_fee_id: fee.id,
        course_fee_structures_id: fee.course_fee_structures_id || null,
        name: userData?.full_name || '',
        email: userData?.email || '',
        phone: userData?.phone || '',
        surl: window.location.origin + '/payment-success',
        furl: window.location.origin + '/course-fees',
      };

      // Initiate payment
      const response = await apiService.initiateEasebuzzPayment(paymentData);

      if (response.success && response.data && response.data.success) {
        const { access_token, merchant_key } = response.data.data;

        // Validate required fields
        if (!access_token || !merchant_key) {
          setError('Payment initialization failed. Please try again.');
          setPaymentLoading(prev => ({ ...prev, [feeId]: false }));
          return;
        }

        // Load EASEBUZZ script if not already loaded
        if (!window.EasebuzzCheckout) {
          const script = document.createElement('script');
          script.src = 'https://easypay.easebuzz.in/payment/v1/easebuzz-checkout.js';
          script.async = true;
          document.body.appendChild(script);
          
          // Wait for script to load
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            setTimeout(() => reject(new Error('Script load timeout')), 10000);
          });
        }

        // Wait a bit for EASEBUZZ to initialize
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!window.EasebuzzCheckout) {
          setError('Payment gateway not loaded. Please refresh the page.');
          setPaymentLoading(prev => ({ ...prev, [feeId]: false }));
          return;
        }

        const environment = response.data.data.environment || 'test';
        const easebuzzCheckout = new window.EasebuzzCheckout(merchant_key, environment);

        const easebuzzOptions = {
          access_key: access_token,
          onResponse: (response_data) => {
            console.log('EASEBUZZ payment response:', response_data);
            // Handle payment response with verification
            handleEasebuzzPaymentCallback(response_data, fee);
          },
          theme: '#3399cc'
        };

        easebuzzCheckout.initiatePayment(easebuzzOptions);
      } else {
        setError(response.data?.message || response.message || 'Failed to initiate payment. Please try again.');
        setPaymentLoading(prev => ({ ...prev, [feeId]: false }));
      }
    } catch (err) {
      console.error('Payment initiation error:', err);
      setError('Error initiating payment. Please try again.');
      setPaymentLoading(prev => ({ ...prev, [feeId]: false }));
    }
  };

  return (
    <>
      {/* Course Fees List Section */}
      <section className="section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="mb-4">
                <h3>Course Fees</h3>
                <p className="text-muted">View your course fee structure and payment details</p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error} 
                  &nbsp;
                  <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>
                    Refresh
                  </button>
                </div>
              )}

              {/* Payment Success Display */}
              {paymentSuccess && (
                <div className="alert alert-success" role="alert">
                  <i className="bi bi-check-circle me-2"></i>
                  {paymentSuccess}
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading course fees...</p>
                </div>
              )}

              {/* Course Fees Table */}
              {!loading && !error && (
                <>
                  {(!courseFees || courseFees.length === 0) ? (
                    <div className="text-center py-5">
                      <i className="bi bi-receipt fs-1 text-muted mb-3"></i>
                      <h5 className="text-muted">No course fees found</h5>
                      <p className="text-muted">You don't have any course fees records yet.</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-hover table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>ID</th>
                              <th>Course Session</th>
                              <th>Year / Semester</th>
                              <th>Fee Offered</th>
                              <th>Received Amount</th>
                              <th>Pending Amount</th>
                              <th>Comment</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {courseFees.map((fee) => {
                              const offered = parseFloat(fee.fee_offered) || 0;
                              const received = parseFloat(fee.received_amt) || 0;
                              const pending = offered - received;
                              
                              return (
                                <tr key={fee.id}>
                                  <td>
                                    <code className="text-primary">{fee.id}</code>
                                  </td>
                                  <td>
                                    <span className="fw-semibold">{formatCourseSession(fee.course_session)}</span>
                                  </td>
                                  <td>
                                    {fee.course_fee_structure ? (
                                      <span className="badge bg-info">
                                        Semester {fee.course_fee_structure.semester_number}
                                      </span>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                  </td>
                                  
                                  <td>
                                    <strong className="text-success">{formatCurrency(fee.fee_offered)}</strong>
                                  </td>
                                  <td>
                                    <strong className="text-primary">{formatCurrency(fee.received_amt)}</strong>
                                  </td>
                                  <td>
                                    <strong className={pending > 0 ? 'text-warning' : 'text-success'}>
                                      {formatCurrency(pending)}
                                    </strong>
                                  </td>
                                  <td>
                                    {fee.comment ? (
                                      <span className="text-muted">{fee.comment}</span>
                                    ) : (
                                      <span className="text-muted fst-italic">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {pending > 0 ? (
                                      <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => handlePayNow(fee)}
                                        disabled={paymentLoading[fee.id]}
                                      >
                                        {paymentLoading[fee.id] ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Processing...
                                          </>
                                        ) : (
                                          'Pay Now'
                                        )}
                                      </button>
                                    ) : (
                                      <span className="badge bg-success">Paid</span>
                                    )}
                                  </td>
                                  
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="table-light">
                            <tr>
                              <td colSpan="3" className="text-end fw-bold">Total Fee Offered:</td>
                              <td className="fw-bold text-success">
                                {formatCurrency(
                                  courseFees.reduce((sum, fee) => sum + (parseFloat(fee.fee_offered) || 0), 0)
                                )}
                              </td>
                              <td colSpan="5"></td>
                            </tr>
                            <tr>
                              <td colSpan="4" className="text-end fw-bold">Total Received:</td>
                              <td className="fw-bold text-primary">
                                {formatCurrency(
                                  courseFees.reduce((sum, fee) => sum + (parseFloat(fee.received_amt) || 0), 0)
                                )}
                              </td>
                              <td colSpan="5"></td>
                            </tr>
                            <tr>
                              <td colSpan="4" className="text-end fw-bold">Total Pending:</td>
                              <td className="fw-bold text-warning">
                                {formatCurrency(
                                  courseFees.reduce((sum, fee) => {
                                    const offered = parseFloat(fee.fee_offered) || 0;
                                    const received = parseFloat(fee.received_amt) || 0;
                                    return sum + (offered - received);
                                  }, 0)
                                )}
                              </td>
                              <td colSpan="5"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      
                      {/* Pagination */}
                      {pagination && pagination.last_page > 1 && (
                        <nav aria-label="Course fees pagination" className="mt-4">
                          <ul className="pagination justify-content-center">
                            <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page === 1}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                              <li key={page} className={`page-item ${page === pagination.current_page ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ))}
                            
                            <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page === pagination.last_page}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CourseFeesPage;

